/* eslint-disable no-confusing-arrow */
import React from 'react';
import _has from 'lodash/has';
import {
  Layout,
  View,
  Card,
  Stack,
  Text,
  TextField,
  AutoSavedSelectControl,
  AutoSavedFilteredSelectControl,
  withTranslation,
  formatNumber,
  formatCurrency,
} from '@piwikpro/ui-components';
import { ConfigService, errorBoundary, withInject } from '@piwikpro/platform';
import { Users } from '@piwikpro/users-crate';
import { FormikActions, FormikValues } from 'formik';
import { TFunction } from 'react-i18next';

type SelectItem = {
  id: string
  name: string
  currency: string
  value: string
}

export interface IProfileProps {
  userLanguage: string
  userEmail: string
  config: ConfigService
  users: Users
  t: TFunction
  Title: typeof React.Component
}

export interface IProfileState {
  regionSearch: string
}

class Profile extends React.Component<IProfileProps, IProfileState> {
  constructor(props: IProfileProps) {
    super(props);

    this.state = {
      regionSearch: '',
    };

    this.handleLanguageSubmit = this.handleLanguageSubmit.bind(this);
    this.handleRegionSubmit = this.handleRegionSubmit.bind(this);
    this.handleRegionSearch = this.handleRegionSearch.bind(this);
    this.clearSearchInput = this.clearSearchInput.bind(this);
  }


  getAvailableRegions(searchValue: string, items: SelectItem[]) {
    const { userLanguage } = this.props;

    return items.filter(
      item => item.id !== userLanguage.split('-')[1]
        ? this.searchValueMatchesItem(searchValue, item)
        : item,
    );
  }

  handleRegionSearch(e: React.ChangeEvent<{value: string}>) {
    this.setState({
      regionSearch: e.target.value,
    });
  }

  clearSearchInput() {
    this.setState({
      regionSearch: '',
    });
  }

  searchValueMatchesItem(searchValue: string, item: SelectItem) {
    return _has(item, 'name')
      && item.name.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1
      && item.name.length >= searchValue.length;
  }

  async handleLanguageSubmit(values: { language: string }, api: FormikActions<FormikValues>) {
    const { userLanguage, users } = this.props;

    try {
      await users.changeLanguageAndRegion(`${values.language}-${userLanguage.split('-')[1]}`);
    } catch (err) {
      api.setFieldValue('language', userLanguage.split('-')[0]);
    } finally {
      api.resetForm();
    }

    api.setSubmitting(false);
  }

  async handleRegionSubmit(values: { region: string }, api: FormikActions<FormikValues>) {
    const { userLanguage, users } = this.props;

    try {
      await users.changeLanguageAndRegion(`${userLanguage.split('-')[0]}-${values.region}`);
    } catch (err) {
      api.setFieldValue('region', userLanguage.split('-')[1]);
    } finally {
      api.setSubmitting(false);
    }
  }

  render() {
    const {
      userEmail,
      userLanguage,
      t,
      config,
      Title,
    } = this.props;

    const { regionSearch } = this.state;
    const filteredRegions = this.getAvailableRegions(regionSearch, config.get('AVAILABLE_REGIONS'));
    const previewCurrency = filteredRegions.find(
      element => element.id === userLanguage.split('-')[1],
    )?.currency;

    return (
      <Title part={t('profile.title')}>
        <View>
          <View.Header title={t('profile.title')} />
          <View.Body>
            <Layout>
              <Layout.AnnotatedSection
                title={t('profile.general-settings.title')}
              />
              <Layout.Column dominant>
                <Card>
                  <Card.Section>
                    <TextField
                      disabled
                      label={t('profile.form.email.title')}
                      value={userEmail}
                      name="email"
                    />
                  </Card.Section>
                </Card>
              </Layout.Column>
            </Layout>
            <Layout>
              <Layout.AnnotatedSection
                title={t('profile.language-and-region.title')}
              />
              <Layout.Column dominant>
                <Card>
                  <Card.Section>
                    <Stack vertical spacing="wide">
                      <Stack.Item fill>
                        <AutoSavedSelectControl
                          id="profile-language-form"
                          initialValue={userLanguage.split('-')[0]}
                          options={[{
                            id: 'user-profile-lang-field',
                            items: config.get('AVAILABLE_LANGUAGES'),
                          }]}
                          label={t('profile.form.language.title')}
                          name="language"
                          autoFocus
                          onSubmit={this.handleLanguageSubmit}
                        />
                      </Stack.Item>
                      <Stack.Item fill>
                        <AutoSavedFilteredSelectControl
                          id="profile-region-form"
                          initialValue={userLanguage.split('-')[1]}
                          value={userLanguage.split('-')[1]}
                          options={[{
                            id: 'user-profile-region-field',
                            items: filteredRegions,
                          }]}
                          search={regionSearch}
                          onSearch={this.handleRegionSearch}
                          onOpen={this.clearSearchInput}
                          label={t('profile.form.region.title')}
                          name="region"
                          onSubmit={this.handleRegionSubmit}
                          getSelectedName={(value: string) => filteredRegions.find(
                            option => value === option.id,
                          )?.name}
                        />
                      </Stack.Item>
                      <Stack.Item fill>
                        <Stack horizontalAlignment="middle">
                          <Stack.Item>
                            <Text color="InkLighter" dataId="TextStyle">
                              {t('components:date.short', { date: new Date() }) as string}
                            </Text>
                          </Stack.Item>
                          <Stack.Item>
                            <Text color="InkLighter" dataId="TextStyle">
                              {t('components:date.long', { date: new Date() }) as string}
                            </Text>
                          </Stack.Item>
                          <Stack.Item>
                            <Text color="InkLighter" dataId="TextStyle">
                              {formatNumber(Number(123456789))}
                            </Text>
                          </Stack.Item>
                          {previewCurrency && (
                            <Stack.Item>
                              <Text color="InkLighter" dataId="TextStyle">
                                {formatCurrency(456789, {
                                  currency: previewCurrency,
                                })}
                              </Text>
                            </Stack.Item>
                          )}
                        </Stack>
                      </Stack.Item>
                    </Stack>
                  </Card.Section>
                </Card>
              </Layout.Column>
            </Layout>
          </View.Body>
        </View>
      </Title>
    );
  }
}

export default withInject({
  config: 'config',
  Title: 'TitleCrate.components.Title',
  users: 'UsersCrate.users',
})(
  withTranslation('profile')(errorBoundary(Profile)),
);
